<template>
  <v-container fluid class="recording-container pa-0 ma-0">
    <!-- Modern Header Section -->
    <div v-if="showHeader" class="header-section mb-6">
      <v-btn icon small class="header-close-btn" @click="dismissHeader">
        <v-icon color="white">mdi-close</v-icon>
      </v-btn>
      <div class="d-flex align-center mb-4">
        <div class="header-icon-wrapper mr-4">
          <v-icon size="32" color="#FFFFFF">mdi-record-circle</v-icon>
        </div>
        <div>
          <h1 class="page-title mb-1">Call Recording</h1>
          <p class="page-subtitle mb-0">
            Record, store, and manage all your call recordings in one place
          </p>
        </div>
        <v-spacer></v-spacer>
        <div class="d-none d-sm-flex">
          <v-chip
            small
            class="mr-2"
            color="rgba(255,255,255,0.2)"
            text-color="white"
          >
            <v-icon small left>mdi-microphone</v-icon>
            Recording
          </v-chip>
        </div>
      </div>
      <v-row class="feature-pills ma-0">
        <v-col cols="6" sm="3" class="pa-1">
          <div class="feature-pill">
            <v-icon small class="mr-2" color="white">mdi-cloud-upload</v-icon>
            <span>Cloud Storage</span>
          </div>
        </v-col>
        <v-col cols="6" sm="3" class="pa-1">
          <div class="feature-pill">
            <v-icon small class="mr-2" color="white">mdi-cellphone</v-icon>
            <span>Mobile Sync</span>
          </div>
        </v-col>
        <v-col cols="6" sm="3" class="pa-1">
          <div class="feature-pill">
            <v-icon small class="mr-2" color="white">mdi-play-circle</v-icon>
            <span>Playback</span>
          </div>
        </v-col>
        <v-col cols="6" sm="3" class="pa-1">
          <div class="feature-pill">
            <v-icon small class="mr-2" color="white">mdi-download</v-icon>
            <span>Download</span>
          </div>
        </v-col>
      </v-row>
    </div>

    <!-- Content Section -->
    <div style="padding: 0 12px;">
      <v-alert
        v-if="
          ($store.getters.companyDetails.usage /
            $store.getters.companyDetails.plan.storageLimit) *
            100 >=
            90
        "
        dense
        text
        type="warning"
        color="orange darken-2"
        border="left"
        class="mb-4"
      >
        Your storage is almost full. Please buy some additional storage to avoid
        missing out recordings
      </v-alert>

      <v-row justify="center" class="mb-5">
        <v-col cols="12" sm="6" md="6">
          <v-card
            class="recording-card"
            :class="{ 'active-card': selectedOption === 'mobile' }"
            style="border-radius: 12px!important;"
            elevation="3"
            hover
            @click="selectOption('mobile')"
          >
            <v-card-title class="card-title">
              <v-icon
                size="28"
                :color="selectedOption === 'mobile' ? 'white' : '#1976d2'"
                >mdi-cellphone</v-icon
              >
              <span class="ml-2">Mobile Call Recording</span>
            </v-card-title>
            <v-card-text>
              <div class="feature-content">
                <div class="feature-list">
                  <v-list dense class="transparent">
                    <v-list-item
                      v-for="(feature, i) in [
                        'Sync your phone\'s internal call records',
                        'Works on selected devices',
                        'Comes included with your GoDial plan',
                      ]"
                      :key="i"
                      class="px-0"
                    >
                      <v-list-item-icon class="mr-2">
                        <v-icon
                          :color="
                            selectedOption === 'mobile' ? 'white' : '#2E7D32'
                          "
                          >mdi-check-circle</v-icon
                        >
                      </v-list-item-icon>
                      <v-list-item-content>
                        <v-list-item-title
                          :class="{
                            'white--text': selectedOption === 'mobile',
                          }"
                          class="text-wrap"
                          >{{ feature }}</v-list-item-title
                        >
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </div>
                <div class="video-preview-container">
                  <div
                    class="video-preview"
                    @click.stop="
                      openYouTube('https://www.youtube.com/watch?v=1glIP7nby8A')
                    "
                  >
                    <v-btn
                      icon
                      large
                      :color="selectedOption === 'mobile' ? 'white' : 'primary'"
                    >
                      <v-icon large>mdi-play-circle</v-icon>
                    </v-btn>
                  </div>
                </div>
              </div>

              <v-divider
                class="my-3"
                :class="{ white: selectedOption === 'mobile' }"
              ></v-divider>

              <div class="switch-row">
                <div
                  class="switch-text"
                  :class="{ 'white--text': selectedOption === 'mobile' }"
                >
                  Enable Mobile Recording
                </div>
                <div class="switch-controls">
                  <v-switch
                    v-model="recordCallsWithTrueCaller"
                    inset
                    dense
                    hide-details
                    :color="selectedOption === 'mobile' ? 'white' : 'primary'"
                    @change="updateRecordCalls"
                  >
                  </v-switch>
                  <v-btn
                    text
                    :color="selectedOption === 'mobile' ? 'white' : 'primary'"
                    @click.stop="learnMore('mobile')"
                  >
                    LEARN MORE
                  </v-btn>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col
          v-if="companyDetails.country === 'India'"
          cols="12"
          sm="6"
          md="6"
        >
          <v-card
            class="recording-card"
            :class="{ 'active-card': selectedOption === 'cloud' }"
            style="border-radius: 12px!important;"
            elevation="3"
            hover
            @click="selectOption('cloud')"
          >
            <v-card-title class="card-title">
              <v-icon
                size="28"
                :color="selectedOption === 'cloud' ? 'white' : '#1976d2'"
                >mdi-cloud</v-icon
              >
              <span class="ml-2">Cloud Call Recording</span>
            </v-card-title>
            <v-card-text>
              <div class="feature-content">
                <div class="feature-list">
                  <v-list dense class="transparent">
                    <v-list-item
                      v-for="(feature, i) in [
                        'Merges customer\'s call with our dedicated recording line',
                        'Works on all devices',
                        'Comes at an extra cost',
                      ]"
                      :key="i"
                      class="px-0"
                    >
                      <v-list-item-icon class="mr-2">
                        <v-icon
                          :color="
                            selectedOption === 'cloud' ? 'white' : '#2E7D32'
                          "
                          >mdi-check-circle</v-icon
                        >
                      </v-list-item-icon>
                      <v-list-item-content>
                        <v-list-item-title
                          :class="{ 'white--text': selectedOption === 'cloud' }"
                          >{{ feature }}</v-list-item-title
                        >
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </div>
                <div
                  class="video-preview"
                  @click.stop="
                    openYouTube('https://www.youtube.com/watch?v=_ypOdJj_Dm4')
                  "
                >
                  <v-btn
                    icon
                    large
                    :color="selectedOption === 'cloud' ? 'white' : 'primary'"
                  >
                    <v-icon large>mdi-play-circle</v-icon>
                  </v-btn>
                </div>
              </div>

              <v-divider
                class="my-3"
                :class="{ white: selectedOption === 'cloud' }"
              ></v-divider>

              <div class="switch-row">
                <div
                  class="switch-text"
                  :class="{ 'white--text': selectedOption === 'cloud' }"
                >
                  Enable Cloud Recording
                </div>
                <div class="switch-controls">
                  <v-switch
                    v-model="recordCloud"
                    inset
                    dense
                    hide-details
                    :color="selectedOption === 'cloud' ? 'white' : 'primary'"
                    @change="setDefaultDialer('Phone Dialer with Rec')"
                  >
                  </v-switch>
                  <v-btn
                    text
                    :color="selectedOption === 'cloud' ? 'white' : 'primary'"
                    @click.stop="learnMore('cloud')"
                  >
                    LEARN MORE
                  </v-btn>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
      <v-card elevation="0" flat class="customBorderRadius">
        <!-- Call Recording card  -->
        <v-layout class="bg-gray" row wrap>
          <v-flex xs12 sm12 md12 style="padding: 1%;">
            <v-card class="customBorderRadius mb-2">
              <v-card-title>
                <v-icon large>mdi-headphones-settings</v-icon>
                <span class="title" style="padding-left: 5px">
                  Call Recording</span
                >
              </v-card-title>
              <v-card-text v-if="selectedOption === 'mobile'">
                <v-row>
                  <!-- Text Content -->
                  <v-col cols="12" md="6">
                    <div align="center">
                      <h2 style="padding-bottom: 1.5%;"></h2>
                    </div>
                    <div style="text-align: left;">
                      GoDial automatically syncs call recordings on Android
                      phones with built-in call recording. After each call, it
                      accesses your phone's recording folder and transfers the
                      file to GoDial
                      <br /><br />
                      <b>Important: </b> Some phones don't have call recording,
                      or they block other apps from accessing recordings (like
                      MI, Realme, Vivo, Oppo phones). In these cases, GoDial
                      can't sync..
                    </div>
                  </v-col>

                  <!-- Enable Recording Switch -->
                  <v-col cols="12" md="6">
                    <v-list-item class="recordPadding">
                      <v-list-item-icon>
                        <v-icon style="padding:2%"
                          >mdi-play-circle-outline</v-icon
                        >
                      </v-list-item-icon>
                      <v-list-item-content>
                        <v-list-item-title>Enable Recordings</v-list-item-title>
                      </v-list-item-content>
                      <v-list-item-action>
                        <v-switch
                          v-model="recordCallsWithTrueCaller"
                          class="mt-5"
                          inset
                          @change="updateRecordCalls"
                        ></v-switch>
                      </v-list-item-action>
                    </v-list-item>

                    <v-alert type="info" class="mt-5">
                      Call Recording is a free feature. We do not charge you for
                      this feature at the moment. It is experimental and does
                      not work on all phones.
                    </v-alert>
                  </v-col>
                </v-row>
              </v-card-text>

              <v-card-text v-if="selectedOption != 'mobile'">
                <div align="center">
                  <h2 style="padding-bottom: 1.5%;"></h2>
                </div>
                <div align="center">
                  GoDial Cloud Call Recording is a key feature. It allows you to
                  record calls directly within the GoDial app.Your calls are
                  securely recorded and stored in the cloud, accessible from any
                  device with an internet connection. This eliminates the need
                  for local storage or manual transfers.
                </div>
              </v-card-text>
            </v-card>
          </v-flex>
        </v-layout>
      </v-card>
      <!-- File Part -->
      <v-layout class="bg-gray">
        <v-flex xs12 sm12 md12>
          <v-card class="customBorderRadius mb-2">
            <v-card-title>
              <v-icon large>mdi-file</v-icon>
              <span class="title" style="padding-left: 5px">Files</span>
            </v-card-title>
            <v-card-text>
              <v-card outlined class="mb-4 storage-stats">
                <v-card-text>
                  <v-row>
                    <v-col cols="12" sm="4">
                      <div class="text-center">
                        <div class="text-h6 mb-1">Total Files</div>
                        <div class="text-h4">{{ totalRecords }}</div>
                      </div>
                    </v-col>
                    <v-col cols="12" sm="4">
                      <div class="text-center">
                        <div class="text-h6 mb-1">Storage Used</div>
                        <div
                          v-if="$store.getters.companyDetails.usage < 1024"
                          class="text-h4"
                        >
                          {{
                            mathfloorCustom($store.getters.companyDetails.usage)
                          }}
                        </div>
                        <div v-else class="text-h4">
                          {{
                            (
                              $store.getters.companyDetails.usage / 1024
                            ).toFixed(2)
                          }}GB
                        </div>
                      </div>
                    </v-col>
                    <v-col cols="12" sm="4">
                      <div class="text-center">
                        <div class="text-h6 mb-1">Storage Left</div>
                        <div
                          v-if="
                            $store.getters.companyDetails.plan.storageLimit -
                              ($store.getters.companyDetails.usage || 0) <
                              1024
                          "
                          class="text-h4"
                        >
                          {{
                            mathfloorCustom(
                              $store.getters.companyDetails.plan.storageLimit -
                                ($store.getters.companyDetails.usage != null
                                  ? $store.getters.companyDetails.usage
                                  : 0),
                            )
                          }}
                        </div>
                        <div v-else class="text-h4">
                          {{
                            (
                              ($store.getters.companyDetails.plan.storageLimit -
                                ($store.getters.companyDetails.usage || 0)) /
                              1024
                            ).toFixed(2)
                          }}GB
                        </div>
                      </div>
                    </v-col>
                  </v-row>
                  <v-row class="mt-2">
                    <v-col cols="12">
                      <v-progress-linear
                        :value="
                          ($store.getters.companyDetails.usage /
                            $store.getters.companyDetails.plan.storageLimit) *
                            100
                        "
                        height="20"
                        rounded
                        striped
                      >
                        <template v-slot:default="{ value }">
                          <span class="white--text"
                            >{{ Math.ceil(value) }}%</span
                          >
                        </template>
                      </v-progress-linear>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>

              <v-data-table
                dense
                elevation="1"
                class="hover compact-table"
                :headers="fileTableHeaders"
                :items="files"
                :loading="loading"
                :items-per-page="records"
                item-key="id"
                hide-default-footer
              >
                <template v-slot:item.name="{ item }">
                  <v-tooltip bottom max-width="500">
                    <template v-slot:activator="{ on, attrs }">
                      <span v-bind="attrs" class="file-name-cell" v-on="on">
                        {{ formatFileName(item.name) }}
                      </span>
                    </template>
                    <span style="word-break: break-all;">{{ item.name }}</span>
                  </v-tooltip>
                </template>

                <template v-slot:item.downlaod="{ item }">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        icon
                        small
                        color="primary"
                        v-bind="attrs"
                        v-on="on"
                        @click="download(item.name, 'phone')"
                      >
                        <v-icon small>mdi-cloud-download</v-icon>
                      </v-btn>
                    </template>
                    <span>Download</span>
                  </v-tooltip>
                </template>

                <template v-slot:item.delete="{ item }">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        icon
                        small
                        color="error"
                        v-bind="attrs"
                        v-on="on"
                        @click="removeRecording(item.id)"
                      >
                        <v-icon small>mdi-trash-can-outline</v-icon>
                      </v-btn>
                    </template>
                    <span>Delete</span>
                  </v-tooltip>
                </template>
              </v-data-table>
              <v-divider style="margin-bottom: 10px"></v-divider>
            </v-card-text>
            <v-card-text>
              <div class="v-data-footer">
                <div class="v-data-footer__select">
                  Rows per page:
                  <div
                    class="v-input v-input--hide-details v-input--is-label-active v-input--is-dirty theme--light v-text-field v-text-field--is-booted v-select"
                  >
                    <v-select
                      v-model="records"
                      :items="recordsPerPage"
                      style="margin: 5px"
                      @change="pagination"
                    ></v-select>
                  </div>
                </div>
                <div class="v-data-footer__select">
                  <div class="v-data-footer__icons-before">
                    <v-btn
                      :disabled="page == 1 ? true : false"
                      color="primary"
                      fab
                      small
                      text
                      @click="prevSet"
                    >
                      <v-icon :size="24">arrow_left</v-icon>
                    </v-btn>
                  </div>
                  <div class="v-data-footer__pagination">
                    {{ `${page}/${pageCount} of ${totalRecords}` }}
                  </div>
                  <div class="v-data-footer__icons-after">
                    <v-btn
                      :disabled="page >= pageCount ? true : false"
                      color="primary"
                      fab
                      small
                      text
                      @click="nextSet"
                    >
                      <v-icon :size="24">arrow_right</v-icon>
                    </v-btn>
                  </div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-flex>
      </v-layout>

      <!-- Agents Part -->
      <v-layout v-if="selectedOption != 'mobile'" class="bg-gray">
        <v-flex xs12 sm12 md12>
          <v-card class="customBorderRadius mb-2">
            <v-card-title>
              <v-icon large>mdi-account-multiple</v-icon>
              <v-row justify="end">
                <v-col style="padding-top: 15px;">Agents</v-col>
              </v-row>
            </v-card-title>

            <v-card-text>
              <v-data-table
                dense
                elevation="1"
                class="hover"
                :headers="agentTableHeaders"
                :items="allUsers"
                :loading="loading"
                :items-per-page="pageAgents.records"
                item-key="id"
                :server-items-length="pageAgents.totalRecords"
                hide-default-footer
              >
                <template v-slot:item.edit="{ item }">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        icon
                        color="primary"
                        v-bind="attrs"
                        v-on="on"
                        @click="editAgent(item)"
                      >
                        <v-icon>mdi-pencil</v-icon>
                      </v-btn>
                    </template>
                    <span>Edit Agent</span>
                  </v-tooltip>
                </template>
              </v-data-table>
            </v-card-text>

            <!-- Pagination Controls (Same as Files Table) -->
            <v-card-text>
              <div class="v-data-footer">
                <div class="v-data-footer__select">
                  Rows per page:
                  <div
                    class="v-input v-input--hide-details v-input--is-label-active theme--light v-text-field v-select"
                  >
                    <v-select
                      v-model="pageAgents.records"
                      :items="pageAgents.recordsPerPage"
                      style="margin: 5px"
                      @change="paginationAgent"
                    ></v-select>
                  </div>
                </div>
                <div class="v-data-footer__select">
                  <div class="v-data-footer__icons-before">
                    <v-btn
                      :disabled="pageAgents.page == 1"
                      color="primary"
                      fab
                      small
                      text
                      @click="prevSetAgent"
                    >
                      <v-icon :size="24">arrow_left</v-icon>
                    </v-btn>
                  </div>
                  <div class="v-data-footer__pagination">
                    {{
                      `${pageAgents.page}/${pageAgents.pageCount} of ${pageAgents.totalRecords}`
                    }}
                  </div>
                  <div class="v-data-footer__icons-after">
                    <v-btn
                      :disabled="pageAgents.page >= pageAgents.pageCount"
                      color="primary"
                      fab
                      small
                      text
                      @click="nextSetAgent"
                    >
                      <v-icon :size="24">arrow_right</v-icon>
                    </v-btn>
                  </div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-flex>
      </v-layout>

      <!-- Edit Dialog -->
      <v-dialog
        v-model="editDialog"
        max-width="600"
        content-class="custom-dialog"
      >
        <v-card class="customBorderRadius custom-card">
          <v-card-title>Edit Agent Number</v-card-title>
          <v-card-text>
            <vue-tel-input
              v-model="editedAgent.phone"
              :valid-characters-only="true"
              class="secondary--text"
              label="Phone Number"
              :default-country="defaultCountry"
              @input="getPhoneNumber"
            ></vue-tel-input>
          </v-card-text>
          <v-card-actions>
            <v-btn color="red" text @click="editDialog = false">Cancel</v-btn>
            <v-btn color="green" text @click="saveAgent">Save</v-btn>
          </v-card-actions>
          <v-alert class="mx-2" dense border="left" type="warning">
            Important: To enable cloud call recording, ensure that the phone
            number entered here matches the number the agent is using to make
            calls.
          </v-alert>
        </v-card>
      </v-dialog>

      <v-layout v-if="selectedOption === 'mobile'">
        <v-flex xs12 sm12 md12>
          <v-card class="customBorderRadius" elevation="2">
            <v-card-title>
              <v-icon large>mdi-database-plus</v-icon>
              <span class="title" style="padding-left: 5px"
                >Upgrade Storage</span
              >
            </v-card-title>
            <v-card-text>
              <div>
                <v-layout row wrap style="margin: 0px">
                  <v-flex xs12 sm12 md12 style="padding: 0%">
                    <v-container justify="center">
                      <h6
                        class="title font-weight-light"
                        style="margin-top: 5px; text-align: center"
                      >
                        <!-- <h5 class="title font-weight-light">
                          {{ Math.round(addStorage / 10) }}GB
                        </h5> -->
                        Please contact us at support@godial.cc
                      </h6>
                      <v-layout row wrap style="padding-bottom: 4%;">
                        <v-flex xs12 sm12 m12> </v-flex>
                      </v-layout>

                      <v-layout style="justify-content: center">
                        <v-btn color="green" style="color: white"
                          ><a
                            href="mailto:support@godial.cc"
                            style="color: white;text-decoration:none;"
                            >Contact Us</a
                          ></v-btn
                        >
                      </v-layout>
                      <!-- <v-layout style="justify-content: center">
                        <v-btn
                          color="green"
                          style="color: white"
                          :disabled="true"
                          @click="upgradeStorage"
                          >Upgrade
                        </v-btn>
                      </v-layout> -->
                    </v-container>
                  </v-flex>
                </v-layout>
              </div>
            </v-card-text>

            <v-divider></v-divider>

            <v-card-text>
              <v-expansion-panels v-model="panel" multiple flat>
                <v-expansion-panel>
                  <v-expansion-panel-header
                    ><b
                      >How do I enable call recordings?</b
                    ></v-expansion-panel-header
                  >
                  <v-expansion-panel-content>
                    To enable call recordings enable it in the agent/member's
                    setting ( Team and Member page) and also enable it in the
                    recording page (above). Once enable the agent/member may be
                    required to re login to the application.
                  </v-expansion-panel-content>
                </v-expansion-panel>

                <v-expansion-panel>
                  <v-expansion-panel-header
                    ><b>Which phones work?</b></v-expansion-panel-header
                  >
                  <v-expansion-panel-content>
                    Most phones that support call recording by default will
                    work. Some phones may be recording the close by storing it
                    at an unaccessible location (for example MI Android 12
                    devices). We do not provide individual level support for
                    phones, incase you may think your device should work, send
                    us an email at support@godial.cc with your device details
                    and path to recording.
                  </v-expansion-panel-content>
                </v-expansion-panel>

                <v-expansion-panel>
                  <v-expansion-panel-header
                    ><b
                      >Why does it not work on all phones</b
                    ></v-expansion-panel-header
                  >
                  <v-expansion-panel-content>
                    Since GoDial is a SIM based dialer, we rely on your phone to
                    make the calls (hence saving you thousands in cloud calling
                    charges), but by doing this we have to rely on the device to
                    record the calls. iOS does not have any call recording at
                    all, while some Android phones still have it.
                  </v-expansion-panel-content>
                </v-expansion-panel>

                <v-expansion-panel>
                  <v-expansion-panel-header
                    ><b
                      >Call recording is a mandatory feature for me, it should
                      work flawlessly on all phones</b
                    ></v-expansion-panel-header
                  >
                  <v-expansion-panel-content>
                    Incase it is a must need feature, you will need to shell out
                    2-4x the costs and get a cloud telephony integration
                    (exotel, knowlarity, twilio, vonage, etc) done with GoDial,
                    so that calls are placed through these providers. Reach out
                    to us at support@godial.cc, incase your interested. We will
                    also start offering our integrated cloud packages very soon.
                  </v-expansion-panel-content>
                </v-expansion-panel>

                <v-expansion-panel>
                  <v-expansion-panel-header
                    ><b
                      >For how long are recordings stored?</b
                    ></v-expansion-panel-header
                  >
                  <v-expansion-panel-content>
                    Recordings are available till your account is active, once
                    your account has expired recordings will be deleted after 60
                    days.
                  </v-expansion-panel-content>
                </v-expansion-panel>

                <v-expansion-panel>
                  <v-expansion-panel-header
                    ><b
                      >How much will we be charged for storage?</b
                    ></v-expansion-panel-header
                  >
                  <v-expansion-panel-content>
                    Currently our pricing for storage is not yet decided since
                    this is an experimental feature, Incase you reach your
                    limit, let us know at support@godial.cc
                  </v-expansion-panel-content>
                </v-expansion-panel>

                <v-expansion-panel>
                  <v-expansion-panel-header
                    ><b
                      >Where can I access recordings?</b
                    ></v-expansion-panel-header
                  >
                  <v-expansion-panel-content>
                    Recordings can be played from Master report on each call log
                    and all recording files are listed on Recording page (above)
                  </v-expansion-panel-content>
                </v-expansion-panel>

                <v-expansion-panel>
                  <v-expansion-panel-header
                    ><b
                      >My internal storage is getting full</b
                    ></v-expansion-panel-header
                  >
                  <v-expansion-panel-content>
                    Kindly delete your recordings from the recording folder
                    every few days
                  </v-expansion-panel-content>
                </v-expansion-panel>

                <v-expansion-panel>
                  <v-expansion-panel-header
                    ><b
                      >My phone has recording yet GoDial is not working?</b
                    ></v-expansion-panel-header
                  >
                  <v-expansion-panel-content>
                    We do not provide individual level support for phones,
                    incase you may think your device should work, send us an
                    email at support@godial.cc with your device details and path
                    to recording.
                  </v-expansion-panel-content>
                </v-expansion-panel>

                <v-expansion-panel>
                  <v-expansion-panel-header
                    ><b
                      >How to debug call recordings?
                    </b></v-expansion-panel-header
                  >
                  <v-expansion-panel-content>
                    Please goto the Diagnostics page from the settings menu in
                    the app, ensure all permissions are enabled, then click find
                    path and see if the path to your recording folder has been
                    determined and last few recording files are shown. If the
                    path cannot be found and you think the device can record
                    calls, please mail us with the path to your recording folder
                    and email to support@godial.cc
                  </v-expansion-panel-content>
                </v-expansion-panel>

                <v-expansion-panel>
                  <v-expansion-panel-header
                    ><b
                      >Which permissions are required for this to work?</b
                    ></v-expansion-panel-header
                  >
                  <v-expansion-panel-content>
                    File permissions are mandatory for call recording
                  </v-expansion-panel-content>
                </v-expansion-panel>

                <v-expansion-panel>
                  <v-expansion-panel-header
                    ><b
                      >Is it legal to capture recordings?</b
                    ></v-expansion-panel-header
                  >
                  <v-expansion-panel-content>
                    It depends on your local law, since we as GoDial do not
                    record the calls, we bear no legal responsibilities for it.
                    The call records are only synced from your device to godial.
                  </v-expansion-panel-content>
                </v-expansion-panel>

                <v-expansion-panel>
                  <v-expansion-panel-header
                    ><b
                      >Will personal phone calls also be recorded?</b
                    ></v-expansion-panel-header
                  >
                  <v-expansion-panel-content>
                    Only numbers dialed or disposed through GoDial will be
                    synced.
                  </v-expansion-panel-content>
                </v-expansion-panel>

                <v-expansion-panel>
                  <v-expansion-panel-header
                    ><b
                      >How many calls can I make per member?</b
                    ></v-expansion-panel-header
                  >
                  <v-expansion-panel-content>
                    Unlimited number of calls can be made by a member with the
                    GoDial Cloud Call Recording Subscription.
                  </v-expansion-panel-content>
                </v-expansion-panel>

                <v-expansion-panel>
                  <v-expansion-panel-header
                    ><b
                      >Can I change the limit during my tenure?</b
                    ></v-expansion-panel-header
                  >
                  <v-expansion-panel-content>
                    Yes, you can upgrade or downgrade your plan post the
                    completion of your subscription. For example, if you have
                    taken a Monthly Subscription, you will have to wait for 30
                    days before you can change the plan. Please contact our
                    support team to discuss plan changes and any associated
                    costs.
                  </v-expansion-panel-content>
                </v-expansion-panel>

                <v-expansion-panel>
                  <v-expansion-panel-header
                    ><b
                      >Is Recording available? What is Cloud Recording?</b
                    ></v-expansion-panel-header
                  >
                  <v-expansion-panel-content>
                    Yes, GoDial Cloud Call Recording is a key feature. It allows
                    you to record calls directly within the GoDial app. Your
                    calls are securely recorded and stored in the cloud,
                    accessible from any device with an internet connection. This
                    eliminates the need for local storage or manual transfers.
                  </v-expansion-panel-content>
                </v-expansion-panel>

                <v-expansion-panel>
                  <v-expansion-panel-header
                    ><b
                      >How many contacts can I add?</b
                    ></v-expansion-panel-header
                  >
                  <v-expansion-panel-content>
                    You can add an unlimited number of contacts to your GoDial
                    account. Though we suggest not to keep more than 300,000
                    contacts at a single point of time, as it overloads the
                    server and can lead to longer loading times for lists and
                    reports.
                  </v-expansion-panel-content>
                </v-expansion-panel>

                <v-expansion-panel>
                  <v-expansion-panel-header
                    ><b
                      >Can I cancel the plan in between?</b
                    ></v-expansion-panel-header
                  >
                  <v-expansion-panel-content>
                    Yes, you can choose to discontinue with GoDial at any point
                    of time. Please note - No amount will be refunded for the
                    unused period of the subscription. If you purchase a monthly
                    plan and only use it for a week, no amount will be refunded
                    for the remaining unused period.
                  </v-expansion-panel-content>
                </v-expansion-panel>

                <v-expansion-panel>
                  <v-expansion-panel-header
                    ><b
                      >What devices are compatible with GoDial Cloud Call
                      Recording?</b
                    ></v-expansion-panel-header
                  >
                  <v-expansion-panel-content>
                    GoDial Cloud Call Recording is compatible with all
                    smartphones running on Android.
                  </v-expansion-panel-content>
                </v-expansion-panel>

                <v-expansion-panel>
                  <v-expansion-panel-header
                    ><b
                      >Is there any storage limit for call recordings?</b
                    ></v-expansion-panel-header
                  >
                  <v-expansion-panel-content>
                    No, there is no storage limit for call recordings. However,
                    we encourage responsible data management to ensure optimal
                    performance.
                  </v-expansion-panel-content>
                </v-expansion-panel>

                <v-expansion-panel>
                  <v-expansion-panel-header
                    ><b
                      >How secure is my call recording data?</b
                    ></v-expansion-panel-header
                  >
                  <v-expansion-panel-content>
                    GoDial employs industry-standard security measures to
                    protect your call recordings, including encryption and
                    access controls.
                  </v-expansion-panel-content>
                </v-expansion-panel>

                <v-expansion-panel>
                  <v-expansion-panel-header
                    ><b
                      >Can I download call recordings?</b
                    ></v-expansion-panel-header
                  >
                  <v-expansion-panel-content>
                    Yes, you can download call recordings for easy access and
                    sharing.
                  </v-expansion-panel-content>
                </v-expansion-panel>

                <v-expansion-panel>
                  <v-expansion-panel-header
                    ><b
                      >What happens if I have a poor internet connection during
                      a call?</b
                    ></v-expansion-panel-header
                  >
                  <v-expansion-panel-content>
                    If you encounter poor internet connectivity, the call
                    recording may be interrupted or incomplete. We recommend
                    using a stable Wi-Fi connection for optimal recording
                    quality.
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </v-card-text>
          </v-card>
        </v-flex>
      </v-layout>

      <v-layout v-else row wrap>
        <!-- For cloud recording -->
        <v-flex xs12 sm12 md12 style="padding: 2%;">
          <v-card class="customBorderRadius" elevation="2">
            <v-card-text>
              <v-expansion-panels v-model="panel" multiple>
                <v-expansion-panel>
                  <v-expansion-panel-header
                    ><b
                      >Is Recording available? What is Cloud Recording?</b
                    ></v-expansion-panel-header
                  >
                  <v-expansion-panel-content>
                    Yes, GoDial Cloud Call Recording is a key feature. It allows
                    you to record calls directly within the GoDial app.Your
                    calls are securely recorded and stored in the cloud,
                    accessible from any device with an internet connection. This
                    eliminates the need for local storage or manual transfers.
                  </v-expansion-panel-content>
                </v-expansion-panel>

                <v-expansion-panel>
                  <v-expansion-panel-header
                    ><b
                      >What devices are compatible with GoDial Cloud Call
                      Recording?</b
                    ></v-expansion-panel-header
                  >
                  <v-expansion-panel-content>
                    GoDial Cloud Call Recording is compatible with all
                    smartphones running on Android.
                  </v-expansion-panel-content>
                </v-expansion-panel>

                <v-expansion-panel>
                  <v-expansion-panel-header
                    ><b
                      >Is there any storage limit for call recordings?</b
                    ></v-expansion-panel-header
                  >
                  <v-expansion-panel-content>
                    No, there is no storage limit for call recordings. However,
                    we encourage responsible data management to ensure optimal
                    performance.
                  </v-expansion-panel-content>
                </v-expansion-panel>

                <v-expansion-panel>
                  <v-expansion-panel-header
                    ><b
                      >How secure is my call recording data?</b
                    ></v-expansion-panel-header
                  >
                  <v-expansion-panel-content>
                    GoDial employs industry-standard security measures to
                    protect your call recordings, including encryption and
                    access controls.
                  </v-expansion-panel-content>
                </v-expansion-panel>

                <v-expansion-panel>
                  <v-expansion-panel-header
                    ><b
                      >Can I download call recordings?</b
                    ></v-expansion-panel-header
                  >
                  <v-expansion-panel-content>
                    Yes, you can download call recordings for easy access and
                    sharing.
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </v-card-text>
          </v-card>
        </v-flex>
      </v-layout>

      <!-- Loader -->
      <v-dialog v-model="loader" persistent width="300">
        <v-card color="primarygrad white--text" dark>
          <v-card-text style="padding: 18px 25px">
            <h3 style="margin-bottom: 5px; font-weight: 400; font-size: 16px">
              Please Wait...
            </h3>
            <v-progress-linear
              indeterminate
              color="white"
              class="mb-0"
            ></v-progress-linear>
          </v-card-text>
        </v-card>
      </v-dialog>
    </div>
  </v-container>
</template>

<script>
import moment from "moment-timezone";
import { mapGetters } from "vuex";
import countries from "../js/countries";
export default {
  data() {
    return {
      showHeader: false,
      selectedOption: "mobile",
      recordCallsWithTrueCaller: false,
      recordCloud: false,
      enableRecording: false,
      panel: [],
      addStorage: 0,
      loading: false,
      recordsPerPage: [1, 5, 15, 30, 60],
      loader: false,
      page: 1,
      pageCount: 0,
      totalRecords: 0,
      records: 5,
      fileTableHeaders: [
        {
          text: "File Name",
          align: "start",
          sortable: false,
          value: "name",
          width: "40%",
        },
        { text: "Size", value: "size", sortable: false, width: "20%" },
        { text: "Time", value: "createdOn", sortable: false, width: "25%" },
        { text: "", value: "downlaod", sortable: false, width: "7.5%" },
        { text: "", value: "delete", sortable: false, width: "7.5%" },
      ],
      pageAgents: {
        records: 10, // Default items per page
        totalRecords: 0, // Total number of items (if using API)
        page: 1,
        pageCount: 0,
        recordsPerPage: [5, 10, 25, 50, 100], // Available options
      },
      files: [],
      agents: [],
      agentTableHeaders: [
        { text: "Agent Name", value: "name", align: "start", sortable: true },
        { text: "Phone Number", value: "phone", sortable: false },
        { text: "Edit", value: "edit", sortable: false },
      ],
      editedAgent: {},
      editDialog: false,
      allUsers: [],
    };
  },
  created() {
    this.showHeader =
      localStorage.getItem("recordingHeaderDismissed") !== "true";
    this.fetchFiles();
    this.recordCallsWithTrueCaller = this.$store.state.companyDetails.recordCallsWithTrueCaller;
    if (this.companyDetails.defaultDialer == "Phone Dialer with Rec") {
      this.recordCloud = true;
    }

    // console.log("this.agents: ", this.agents)

    this.getTotalAccountCount();
    this.getUserDetails(
      this.pageAgents.records,
      this.pageAgents.records * this.pageAgents.page - this.pageAgents.records,
    );
  },
  computed: {
    ...mapGetters(["ENDPOINT", "teams", "selectedTeam", "companyDetails"]),
    selectedTeam: {
      get(v) {
        return this.$store.getters.selectedTeam;
      },
      set(v) {
        this.$store.commit("setSelectedTeam", v);
      },
    },
    defaultCountry() {
      const country = countries.find(
        (item) => item.name === this.$store.state.companyDetails.country,
      );
      return country ? country.code : "IN";
    },
  },
  methods: {
    dismissHeader() {
      this.showHeader = false;
      localStorage.setItem("recordingHeaderDismissed", "true");
    },
    refreshAgents() {
      this.pageAgents = {
        records: 10,
        totalRecords: 0,
        page: 1,
        pageCount: 0,
        recordsPerPage: [5, 10, 25, 50, 100],
      };
      this.getTotalAccountCount();
      this.getUserDetails(
        this.pageAgents.records,
        this.pageAgents.records * this.pageAgents.page -
          this.pageAgents.records,
      );
    },
    paginationAgent() {
      this.pageAgents.pageCount = Math.ceil(
        this.pageAgents.totalRecords / this.pageAgents.records,
      );
      if (this.pageAgents.page > this.pageAgents.pageCount) {
        this.pageAgents.page = 1;
      }
      // this.loadAgents(); // Reload data based on new pagination
    },

    prevSetAgent() {
      if (this.pageAgents.page > 1) {
        this.pageAgents.page--;
        this.pagination();
      }
    },

    nextSetAgent() {
      if (this.pageAgents.page < this.pageAgents.pageCount) {
        this.pageAgents.page++;
        this.pagination();
      }
    },

    // loadAgentsAgent() {
    //   // Fetch data based on pagination
    //   console.log(`Fetching agents for page ${this.page} with ${this.records} per page`);
    // },
    editAgent(agent) {
      this.editedAgent = { ...agent };
      this.editDialog = true;
    },
    // Get the number with country code
    getPhoneNumber(e, phone) {
      if (e) {
        return (this.editedAgent.phone = phone.number.e164);
      } else {
        return (this.editedAgent.phone = "");
      }
    },
    async saveAgent() {
      // const index = this.agents.findIndex((a) => a.id === this.editedAgent.id);
      // if (index !== -1) {
      //   this.$set(this.agents, index, { ...this.editedAgent });
      // }
      try {
        const data = {
          phone: this.editedAgent.phone,
        };

        var response = await this.$http.patch(
          `${this.ENDPOINT}/accounts/${this.editedAgent.id}`,
          data,
        );

        console.log("updated agnet: ", response.body);
        this.refreshAgents();
        this.$swal({ type: "success", text: "Successfully updated" });
      } catch (error) {
        this.$swal({ type: "error", text: "An error occurred." });
      } finally {
        this.editDialog = false;
      }
    },
    learnMore(option) {
      if (option === "mobile") {
        this.$router.push("/mobile-recording");
      } else if (option === "cloud") {
        this.$router.push("/cloud-recording");
      }
    },
    openYouTube(url) {
      window.open(url, "_blank");
    },
    selectOption(option) {
      this.selectedOption = option;
      this.fetchFiles();
      console.log(`${option} Call Recording Selected`);
    },
    removeRecording(item) {
      console.log("item 123", item);

      this.$http
        .delete(`${this.ENDPOINT}/attachments/${item}`)
        .then((response) => {
          this.loader = false;
          this.fetchFiles();
          this.$swal({ type: "success", text: "Successfully deleted" });
          // this.close();
        })
        .catch((err) => {
          this.loader = false;
          this.$swal({ type: "error", text: "Something went wrong" });
        });
    },
    download(src, dialingOpt) {
      if (src != "") {
        if (dialingOpt == "exotel") {
          console.log(src);
          var a = document.createElement("a");

          a.href = src;
          a.setAttribute("download", src.toString());
          a.target = "_blank";
          a.click();
        } else {
          console.log(src);
          var au = document.createElement("a");

          au.href = `${this.$store.state.ENDPOINT}/storages/get/${src}?access_token=${this.$store.getters.user.token}`;
          au.setAttribute("download", src.toString());
          au.target = "_blank";
          au.click();
        }
      }
    },
    mathfloorCustom(value) {
      console.log("value getting : ", value);
      if (value == 0 || value == null) {
        return "0MB";
      }

      var filterValueInBytes = value * 1024 * 1024;

      var sizes = ["Bytes", "KB", "MB", "GB", "TB"];
      var i = parseInt(
        Math.floor(Math.log(filterValueInBytes) / Math.log(1024)),
      );

      var finalValueData =
        Math.round(filterValueInBytes / Math.pow(1024, i), 2) + "" + sizes[i];

      return finalValueData;
    },
    nextSet() {
      this.page++;
      this.pagination();
    },
    prevSet() {
      this.page--;
      this.pagination();
    },
    async pagination() {
      try {
        this.pageCount = Math.ceil(this.totalRecords / this.records);
        if (this.page > this.pageCount) {
          this.page = 1; //set default
        }
        this.files = await this.fetchFiles(
          this.records,
          this.records * this.page - this.records,
        );

        // Save default pagination of seperate pages
        this.paginate[
          this.$router.currentRoute.name
            .toString()
            .trim()
            .replace(/ /g, "")
            .toLowerCase()
        ] = {
          page: this.page,
          pageCount: this.pageCount,
          records: this.records,
          totalRecords: this.totalRecords,
        };
        this.$store.commit("setPagination", this.paginate);

        return;
      } catch (e) {
        this.loader = false;
      }
    },

    async fetchFiles(limit = 5, skip = 0) {
      // If no parameters provided, get the total count
      if (arguments.length === 0) {
        try {
          const where = {
            companyId: this.$store.getters.user.companyId,
            ...(this.selectedOption === "mobile"
              ? { typeOfFile: "recording" }
              : { type: "exotelRecording", typeOfFile: "attachment" }),
          };

          const response = await this.$http.get(
            `${
              this.$store.getters.ENDPOINT
            }/attachments/count?where=${encodeURIComponent(
              JSON.stringify(where),
            )}`,
          );

          this.totalRecords = response.body.count;
          this.pageCount = Math.ceil(this.totalRecords / this.records);
        } catch (e) {
          console.log(e);
        }
      }

      this.loading = true;
      this.files = [];

      const filter = {
        skip: skip,
        limit: limit,
        order: "createdOn DESC",
        where: {
          companyId: this.$store.getters.user.companyId,
          ...(this.selectedOption === "mobile"
            ? { typeOfFile: "recording" }
            : { type: "exotelRecording", typeOfFile: "attachment" }),
        },
      };

      try {
        const res = await this.$http.get(
          `${
            this.$store.getters.ENDPOINT
          }/attachments?filter=${encodeURIComponent(JSON.stringify(filter))}`,
        );

        this.files = _.map(res.body, (c) => ({
          ...c,
          size: this.mathfloorCustom(c.size),
          createdOn: moment(c.createdOn).format("lll"),
        }));
      } catch (error) {
        console.error("Error fetching files:", error);
      } finally {
        this.loading = false;
      }

      return this.files;
    },

    upgradeStorage() {},
    async refresh() {
      try {
        var filter = {
          include: [{ relation: "plan", scope: { include: ["coupons"] } }],
        };
        var response = await this.$http.get(
          `${this.$store.state.ENDPOINT}/companies/${
            this.$store.getters.user.companyId
          }?filter=${encodeURIComponent(JSON.stringify(filter))}`,
        );
        response = response.body;
        console.log("response.body", response);
        this.$store.commit("setCompany", response); // store this company details
      } catch (e) {
        console.log("Error", e);
      }
    },
    async setDefaultDialer(selectedDialer) {
      const self = this;
      const plansWithRec = [
        "GoDial Monthly With Rec",
        "GoDial HalfYearly With Rec",
        "GoDial Yearly With Rec",
      ];
      console.log("this.recordCloud: ", this.recordCloud);

      // Check if mobile recording is enabled

      if (this.recordCallsWithTrueCaller) {
        self
          .$swal({
            type: "error",
            text: "Please disable Mobile Recording first",
          })
          .then(() => {
            this.recordCloud = false;
          });
        return;
      }
      // Check if current plan supports recording

      if (
        !plansWithRec.some((plan) => plan === this.companyDetails.plan.name)
      ) {
        self
          .$swal({
            type: "error",
            text:
              "Please upgrade your plan to enable Cloud call recording. In case you wish to try out this feature, contact our support",
          })
          .then(() => {
            this.recordCloud = false;
          });
        return;
      }

      this.loader = true;

      if (selectedDialer == "Phone Dialer with Rec") {
        if (!this.recordCloud) {
          selectedDialer = "Phone Dialer";
        }
      }

      self.$http
        .patch(
          `${self.ENDPOINT}/companies/${self.$store.getters.user.companyId}`,
          {
            defaultDialer: selectedDialer,
          },
        )
        .then((response) => {
          self.loader = false;
          console.log("response", response.body);

          self.$swal({
            type: "success",
            text: this.recordCloud
              ? "Recording Enabled successfully"
              : "Recording disabled successfully",
          });

          self.refresh();
        })
        .catch((err) => {
          console.log("err", err);
          self.loader = false;
          self.$swal({
            type: "error",
            text: "Something went wrong. Try again later",
          });
        })
        .finally(() => {
          self.loader = false; // Runs in both success and failure cases
        });
    },
    updateRecordCalls() {
      var self = this;

      self.$http
        .patch(
          `${this.$store.getters.ENDPOINT}/companies/${self.$store.getters.user.companyId}`,
          { recordCallsWithTrueCaller: self.recordCallsWithTrueCaller },
        )
        .then((response) => {
          self.$store.dispatch("getCompanyDetails", {
            companyId: self.$store.getters.user.companyId,
          });
          console.log("response", response.body);
        })
        .catch((err) => {
          self.$swal({
            type: "error",
            text: "Something went wrong. Couldn't Update",
          });
        });
    },

    getTeamIds() {
      var teams = [];
      if (this.selectedTeam == "allTeams") {
        teams = _.map(this.teams, "id");
      } else {
        teams.push(this.selectedTeam);
      }
      teams = _.without(teams, "allTeams");
      return teams;
    },

    getTotalAccountCount() {
      var teamIds = this.getTeamIds();
      if (this.search == undefined || this.search == null) {
        this.search = "";
      }
      this.$http
        .post(`${this.$store.state.ENDPOINT}/accounts/fetchCount-v2`, {
          teams: teamIds,
          search: this.search,
        })
        .then((response) => {
          this.pageAgents.totalRecords = response.body;
          this.pageAgents.pageCount = Math.ceil(
            this.pageAgents.totalRecords / this.pageAgents.records,
          );
        })
        .catch((err) => {
          console.log(err);
        });
    },

    // Fetch lists
    // async fetchLists() {
    //   try {
    //     this.lists = [];
    //     // Teams sections
    //     var teamIds = this.getTeamIds();

    //     var res = await this.$http.post(`${this.ENDPOINT}/lists/fetch`, {
    //       teams: teamIds,
    //     });
    //     this.lists = res.body;

    //     if (this.lists.length < 1) {
    //       return;
    //     }
    //     if (
    //       this.$store.getters.user.role == "Manager" ||
    //       this.$store.getters.user.role == "Teammanager"
    //     ) {
    //       this.lists.push({
    //         id: "allLists",
    //         name: "All Lists",
    //       });
    //     }

    //     if (
    //       _.filter(this.lists, (d) => {
    //         return this.selectedList.toString() === d.id.toString();
    //       }).length < 1
    //     ) {
    //       this.selectedList == "";
    //       this.selectedList = this.lists.length > 0 ? this.lists[0].id : "";
    //     }
    //   } catch (e) {
    //     this.loader = false;
    //     console.log("error in list fetch", e);
    //   }
    // },

    // get all the users by teamsid
    getUserDetails(limit, skip) {
      const self = this;
      // this.loader = true;
      // this.loadingText = "Loading... Please wait";
      // Teams sections
      var teamIds = this.getTeamIds();

      if (limit == undefined || skip == undefined) {
        limit = 10;
        skip = 0;
      }

      if (this.search == undefined || this.search == null) {
        this.search = "";
      }

      this.$http
        .post(`${this.$store.state.ENDPOINT}/accounts/fetch-v2`, {
          teams: teamIds,
          search: this.search,
          limit: limit.toString(),
          skip: skip.toString(),
        })
        .then(async (response) => {
          self.allUsers = response.body;

          _.forEach(self.allUsers, (user) => {
            if (user && user.permissions && user.permissions.enableSessionLock === "false") {
              user.permissions.enableSessionLock = false;
            }
          });

          for (var x in self.allUsers) {
            var listNameAssign = [];

            for (var y in self.allUsers[x].lists) {
              listNameAssign.push(self.allUsers[x].lists[y].name);
            }

            self.allUsers[x].listName = listNameAssign;
          }

          for (var z in self.allUsers) {
            var teamNameAssign = [];
            for (var a in self.allUsers[z].teams) {
              teamNameAssign.push(self.allUsers[z].teams[a].name);
            }

            self.allUsers[z].teamName = teamNameAssign;
          }

          console.log("agents: ", this.allUsers);
          // this.loader = false;
          // this.loadingText = "";
        })
        .catch((err) => {
          console.log(err);
          this.loading = false;
          // this.loadingText = "Error while fetching the users";
          this.loader = false;
          //error
        });
    },
    formatFileName(fileName) {
      if (this.selectedOption === "mobile") {
        if (fileName.length > 30) {
          return `${fileName.substring(0, 15)}...${fileName.slice(-8)}`;
        }
      }
      return fileName;
    },
  },
  watch: {
    selectedOption() {
      this.page = 1; // Reset to first page
      this.fetchFiles(); // Refresh files list
    },
  },
};
</script>

<style scoped>
.recording-container {
  background-color: var(--v-background-base, #f5f5f5);
  min-height: 100vh;
}

.theme--dark .recording-container {
  background-color: #121212 !important;
}

.bg-gray {
  background-color: #f5f5f5;
}

.custom-list {
  list-style: none;
  padding: 0;
}

.custom-card {
  overflow: hidden;
  min-height: 300px;
}

.custom-list li {
  position: relative;
  /* padding-left: 30px; */
  margin-bottom: 10px;
}

.feature-top {
  display: flex;
}

/* background-image: linear-gradient(to right, #37cf37, #37cf37); */

.custom-list li::before {
  content: "\f10c";
  /* Unicode for Material Design Icon (mdi-check-circle) */
  font-family: "Material Design Icons";
  /* color: green; */
  position: absolute;
  left: 0;
  top: 0;
  font-size: 20px;
}

.selectable-card {
  cursor: pointer;
  transition: 0.3s ease;
}

.selectable-card:hover {
  background-color: #f5f5f5;
  transform: scale(1.02);
}

.active-card {
  background: linear-gradient(135deg, #2e7d32, #4caf50) !important;
  border-color: #2e7d32;
  box-shadow: 0 3px 5px -1px rgba(46, 125, 50, 0.2),
    0 6px 10px 0 rgba(46, 125, 50, 0.14), 0 1px 18px 0 rgba(46, 125, 50, 0.12) !important;
}

.active-card .v-card__title,
.active-card .v-list-item__title,
.active-card .switch-text {
  color: white !important;
}

.active-card .v-divider {
  border-color: rgba(255, 255, 255, 0.12) !important;
}

.active-card .v-btn {
  color: white !important;
}

.active-card .v-input--switch {
  color: white !important;
}

.active-card .v-input--switch .v-input__slot {
  color: white !important;
}

.video-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20%;
}

.video-icon div {
  width: 5rem;
  height: 5rem;
  border-radius: 50%;
  /* border: 1px solid black; */
  padding: 0.5rem;
}

.video-icon div img {
  width: 100%;
  object-fit: cover;
}

.content-box {
  width: 80%;
}

.active-card span {
  color: white;
  font-weight: 500;
}

.customMarginBottom {
  margin-bottom: 6px !important;
}

.v-menu__content {
}

@media (min-width: 1200px) {
  .recordPadding {
    padding-left: 30%;
    padding-right: 30%;
  }
}

/* Landscape tablets and medium desktops */
@media (min-width: 992px) and (max-width: 1199px) {
  .recordPadding {
    padding-left: 30%;
    padding-right: 30%;
  }
}

/* Portrait tablets and small desktops */
@media (min-width: 768px) and (max-width: 991px) {
  .recordPadding {
    padding-left: 20%;
    padding-right: 20%;
  }
}

/* Landscape phones and portrait tablets */
@media (max-width: 767px) {
  .recordPadding {
    padding-left: 1%;
    padding-right: 1%;
  }
}

/* Portrait phones and smaller */
@media (max-width: 480px) {
  .recordPadding {
    padding-left: 1%;
    padding-right: 1%;
  }
}

.file-name-cell {
  max-width: 300px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: block;
}

.compact-table ::v-deep .v-data-table__wrapper {
  margin: 0;
}

.compact-table ::v-deep .v-data-table {
  width: 100%;
}

.compact-table ::v-deep .v-data-table td {
  padding: 0 16px !important;
  height: 40px !important;
}

.compact-table ::v-deep .v-data-table th {
  padding: 0 16px !important;
  height: 40px !important;
  white-space: nowrap;
}

.storage-stats {
  background-color: #f8f9fa !important;
  border-radius: 8px;
}

.storage-stats .v-progress-linear {
  border-radius: 4px;
}

.text-h6 {
  color: #666;
  font-weight: 500;
}

.text-h4 {
  color: #2c3e50;
  font-weight: 600;
}

.recording-card {
  transition: all 0.3s ease;
  border: 2px solid transparent;
  height: 100%;
}

.recording-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.1) !important;
}

.card-title {
  font-size: 1.25rem;
  font-weight: 500;
  padding: 20px 15px;
}

.feature-content {
  display: flex;
  align-items: flex-start;
  margin-bottom: 16px;
  position: relative;
  gap: 16px;
  min-height: 100px;
}

.feature-list {
  flex: 1;
  min-width: 0;
  /* This ensures text wrapping works properly */
}

.text-wrap {
  white-space: normal !important;
  word-break: break-word;
  line-height: 1.4;
}

.video-preview-container {
  display: flex;
  align-items: center;
  justify-content: center;
  padding-left: 8px;
  height: 100%;
}

.video-preview {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  cursor: pointer;
  transition: transform 0.2s ease;
}

.video-preview:hover {
  transform: scale(1.1);
}

/* Update v-list-item styles for better text wrapping */
::v-deep .v-list-item {
  min-height: 36px !important;
  flex-wrap: wrap;
}

::v-deep .v-list-item__content {
  flex: 1;
  min-width: 0;
}

::v-deep .v-list-item__title {
  white-space: normal;
  line-height: 1.4;
}

.switch-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 16px;
  min-height: 48px;
}

.switch-text {
  font-size: 0.875rem;
  font-weight: 400;
}

.switch-controls {
  display: flex;
  align-items: center;
  gap: 8px;
}

::v-deep .v-input--switch {
  margin: 0;
  padding: 0;
}

::v-deep .v-input--switch .v-input__slot {
  margin: 0;
}

::v-deep .v-btn {
  text-transform: uppercase;
  letter-spacing: 0.0892857143em;
}

/* Dark Theme Styles */
.theme--dark .customBorderRadius {
  background: #1e1e1e !important;
}

.theme--dark .bg-gray {
  background-color: #121212 !important;
}

.theme--dark .storage-stats {
  background-color: #2d2d2d !important;
  border-color: #404040 !important;
}

.theme--dark .storage-stats .text-h6 {
  color: #a0a0a0 !important;
}

.theme--dark .storage-stats .text-h4 {
  color: #e0e0e0 !important;
}

.theme--dark .v-card {
  background: #1e1e1e !important;
}

.theme--dark .v-card-title,
.theme--dark .v-card-title .title {
  color: #e0e0e0 !important;
}

.theme--dark .v-card-text {
  color: #a0a0a0 !important;
}

.theme--dark .v-data-table {
  background: #1e1e1e !important;
}

.theme--dark .v-data-table th {
  background: #2d2d2d !important;
  color: #e0e0e0 !important;
}

.theme--dark .v-data-table td {
  color: #e0e0e0 !important;
  border-color: #404040 !important;
}

.theme--dark .v-data-footer {
  color: #a0a0a0 !important;
}

.theme--dark .v-data-footer__select {
  color: #a0a0a0 !important;
}

.theme--dark .v-data-footer__pagination {
  color: #e0e0e0 !important;
}

.theme--dark .recording-card:not(.active-card) {
  background: #1e1e1e !important;
  border-color: #404040 !important;
}

.theme--dark .recording-card:not(.active-card) .card-title {
  color: #e0e0e0 !important;
}

.theme--dark .recording-card:not(.active-card) .v-list-item__title {
  color: #e0e0e0 !important;
}

.theme--dark .recording-card:not(.active-card) .switch-text {
  color: #e0e0e0 !important;
}

.theme--dark .recording-card:not(.active-card):hover {
  background: #252525 !important;
}

.theme--dark .v-list-item__title {
  color: #e0e0e0 !important;
}

.theme--dark .v-list-item__content {
  color: #e0e0e0 !important;
}

.theme--dark .v-progress-linear {
  background: #404040 !important;
}

.theme--dark .header-section .page-title {
  color: white !important;
}

.theme--dark .header-section .page-subtitle {
  color: rgba(255, 255, 255, 0.95) !important;
}

.theme--dark .header-section .feature-pill {
  color: white !important;
}

.theme--dark .file-name-cell {
  color: #e0e0e0 !important;
}
</style>
